import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToggle } from "react-use";
import { Dialog, Button, Checkbox, Label, Body2 } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";

const TrashTaskLink = ({ children, onClick, dependenciesCount, hasTracking, trashTrackingObjects, ...props }) => {
  const { t } = useTranslation();
  const [trashTracking, setTrashTracking] = useToggle(trashTrackingObjects);

  const [open, setOpen] = useState(false);

  const handleClick = useCallback((e) => {
    e.preventDefault();
    setOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setOpen(false);
    setTrashTracking(trashTrackingObjects);
  }, [setTrashTracking, trashTrackingObjects]);

  const handleConfirm = useCallback(() => {
    setOpen(false);
    typeof onClick === "function" && onClick(trashTracking);
  }, [onClick, trashTracking]);

  return (
    <>
      {React.cloneElement(children, { onClick: handleClick, ...props })}

      <Dialog
        open={open}
        onClose={handleCancel}
      >
        <Dialog.Title>{t("Delete Task")}</Dialog.Title>
        <Dialog.ContentDivider />
        <Dialog.Content>
          {dependenciesCount ? (
            <>
              <Body2
                weight="bold"
                className="tw-mb-2"
              >
                {t("This task has dependencies")}
              </Body2>
              <Body2 className="tw-mb-6">{t("Deleting the task will disconnect it from its linked tasks.")}</Body2>
            </>
          ) : null}
          {hasTracking ? (
            <>
              <Body2
                weight="bold"
                className="tw-mb-2"
              >
                {t("This task has time records and expenses")}
              </Body2>
              <div
                className={classNames("tw-flex tw-flex-row tw-items-center", {
                  "tw-mb-6": trashTracking,
                })}
              >
                <Checkbox
                  className="tw-mr-2"
                  id="trash-tracking"
                  checked={trashTracking}
                  onChange={setTrashTracking}
                />
                <Label
                  size="regular"
                  htmlFor="trash-tracking"
                  className="tw-cursor-pointer"
                  weight="regular"
                >
                  {t("Move time records and expenses to Trash")}
                </Label>
              </div>
              {!trashTracking && hasTracking ? (
                <Body2 className="tw-mb-6 tw-mt-2">
                  {t(
                    "If not moved to Trash, the recorded time and expenses will be transferred directly to the project they're part of."
                  )}
                </Body2>
              ) : null}
            </>
          ) : null}
          <Body2>{t("The task will remain in Trash for 30 days.")}</Body2>
        </Dialog.Content>
        <Dialog.ContentDivider />
        <Dialog.Actions>
          <Button
            type="button"
            variant="primary"
            className="tw-mr-3"
            onClick={handleConfirm}
          >
            {t("Delete")}
          </Button>
          <Button
            type="button"
            onClick={handleCancel}
            variant="secondary"
          >
            {t("Cancel")}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </>
  );
};

TrashTaskLink.propTypes = {
  dependenciesCount: PropTypes.number,
  hasTracking: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
  trashTrackingObjects: PropTypes.bool,
};

TrashTaskLink.defaultProps = {
  dependenciesCount: 0,
  hasTracking: false,
  trashTrackingObjects: false,
};

export default TrashTaskLink;
